
import { useState } from "react";
import { Dealer } from "@/types/dealer";
import { useDealerDetail } from "./useDealerDetail";
import { isSSR } from "@/lib/isSSR";
import { useParams } from "react-router-dom";
import { useDealerUtils } from "./useDealerUtils";
import { useEmbeddedDealerDetail } from "./useEmbeddedDealerData";

/**
 * Hook that combines server-side and client-side dealer detail data fetching
 * Uses embedded data if available, then falls back to server-side data, and finally to client-side fetching
 */
export function useHydratedDealerDetail() {
  // Get URL parameters
  const params = !isSSR() ? useParams<{ id: string; city: string }>() : { id: '', city: '' };
  const dealerId = params.id || '';
  const citySlug = params.city || '';
  
  // Get the client-side dealer data
  const dealerData = useDealerDetail(dealerId);
  const { formatCityName } = useDealerUtils();
  
  // Get embedded dealer data from HTML
  const { dealer: embeddedDealer, isLoading: embeddedLoading } = useEmbeddedDealerDetail(dealerId);
  
  // Determine which data to use - prioritize embedded data, then fall back to client-side data
  const dealer = embeddedDealer || dealerData.dealer;
  const loading = embeddedLoading && dealerData.loading;
  const error = dealerData.error;
  
  // Get user distance from client-side data (only available on client-side)
  const userDistance = dealerData.userDistance;
  
  // Format city name from slug
  const formattedCity = formatCityName(citySlug);
  
  return {
    dealer,
    loading,
    error,
    dealerId,
    citySlug,
    formattedCity,
    userDistance
  };
}
